import { z } from 'zod';
import { MaintenanceCategory } from '../maintenance.js';
import { isPeriodKey, PERIOD_KINDS } from '../management.js';
import { amount, Id } from './common.js';
import { AtelierFigures, MonthKey } from './metrics.js';

/**
 * Gestion (03/10/2026, décision du porteur) : espace du **gestionnaire** (propriétaire et associés : action
 * `team.manage`), séparé de l'activité quotidienne de l'atelier ; remplace « Chiffres ». Trois lectures :
 * historique des dossiers restitués (`management.history`), activité et volume financier d'une période
 * (`management.activity`), export CSV de l'historique (`management.export`, journalisé). Calculs en SQL
 * `numeric`, périodes en jours locaux du garage. Les montants sont ceux **saisis dans l'outil** (jamais le
 * facturé : Pennylane fait foi, `invoicing` = `null`), retirés sans `amounts.view`.
 */

const Count = z.int().nonnegative();
const DateKey = z.iso.date();
/** Nombre de jours décimal, une décimale (`"3.5"`), ou `null` sans donnée. */
const Days = z.string().regex(/^\d+\.\d$/).nullable();

export const PeriodKind = z.enum(PERIOD_KINDS);

/** Période : `fiscal` (exercice, clé `YYYY` = année du premier mois), `year` (`YYYY`) ou `month` (`YYYY-MM`). Défaut : exercice en cours. */
const periodShape = {
  period: PeriodKind.default('fiscal'),
  key: z.string().max(7).optional(),
};
const checkKey = (v: { period: z.infer<typeof PeriodKind>; key?: string | undefined }, ctx: z.RefinementCtx) => {
  if (v.key !== undefined && !isPeriodKey(v.period, v.key)) {
    ctx.addIssue({ code: 'custom', path: ['key'], message: v.period === 'month' ? 'Mois au format AAAA-MM attendu' : 'Année au format AAAA attendue' });
  }
};

export const ManagementPeriodQuery = z.strictObject(periodShape).superRefine(checkKey);

/** Filtres de l'historique (et de l'export). `status` : `reopened` = restitués après au moins une réouverture. */
const historyFilters = {
  ...periodShape,
  q: z.string().trim().max(100).optional(),
  make: z.string().trim().min(1).max(60).optional(),
  category: MaintenanceCategory.optional(),
  mechanic: Id.optional(),
  status: z.enum(['closed', 'reopened']).optional(),
};
export const ManagementExportQuery = z.strictObject(historyFilters).superRefine(checkKey);
export const ManagementHistoryQuery = z
  .strictObject({
    ...historyFilters,
    page: z.coerce.number().int().min(1).max(10_000).default(1),
    limit: z.coerce.number().int().min(10).max(100).default(25),
  })
  .superRefine(checkKey);

export const PeriodInfo = z.object({ kind: PeriodKind, key: z.string(), label: z.string(), from: DateKey, to: DateKey });
export type PeriodInfo = z.infer<typeof PeriodInfo>;

export const HistoryItem = z.object({
  interventionId: Id,
  receivedAt: z.iso.datetime({ offset: true }),
  releasedAt: z.iso.datetime({ offset: true }),
  /** Jours d'immobilisation (entrée → restitution), une décimale. */
  immobilizedDays: z.string(),
  plate: z.string(),
  make: z.string(),
  model: z.string(),
  customerName: z.string(),
  /** Objet : demande du client à l'entrée. */
  subject: z.string(),
  mechanic: z.string().nullable(),
  categories: z.array(MaintenanceCategory),
  reopened: z.boolean(),
  /** Travaux réalisés saisis (TTC), retiré sans `amounts.view`. */
  workTtc: amount(),
});
export type HistoryItem = z.infer<typeof HistoryItem>;

export const ManagementHistoryOutput = z.object({
  period: PeriodInfo,
  /** Regroupement : nombre de dossiers restitués par exercice (ou par année), le plus récent d'abord. */
  periods: z.array(z.object({ key: z.string(), label: z.string(), count: Count })),
  items: z.array(HistoryItem),
  total: Count,
  page: z.int().min(1),
  pageCount: Count,
  /** Valeurs des filtres (dossiers restitués du garage). */
  facets: z.object({ makes: z.array(z.string()), mechanics: z.array(z.object({ id: Id, name: z.string() })) }),
});
export type ManagementHistoryOutput = z.infer<typeof ManagementHistoryOutput>;

/**
 * Activité d'une période :
 * - `vehiclesReceived` / `vehiclesReleased` : entrées et restitutions horodatées dans la période ;
 * - `interventions` : dossiers ayant au moins une ligne de travaux réalisée dans la période ;
 * - `averageImmobilizationDays` : moyenne (entrée → restitution) des dossiers restitués dans la période ;
 * - `averageDaysToReady` : moyenne (entrée → prêt) des mêmes dossiers, quand « prêt » a été signalé ;
 * - `customers` / `returningCustomers` : clients dont un véhicule est entré dans la période, et parmi eux ceux
 *   qui avaient déjà un dossier plus ancien au garage ;
 * - `appointments` : rendez-vous prévus dans la période (honorés = véhicule entré, annulés, encore prévus).
 */
export const ActivityFigures = z.object({
  vehiclesReceived: Count,
  vehiclesReleased: Count,
  interventions: Count,
  averageImmobilizationDays: Days,
  averageDaysToReady: Days,
  customers: Count,
  returningCustomers: Count,
  appointments: z.object({ honoured: Count, cancelled: Count, planned: Count }),
});
export type ActivityFigures = z.infer<typeof ActivityFigures>;

export const ManagementActivityOutput = z.object({
  timezone: z.string(),
  fiscalYearStartMonth: z.int().min(1).max(12),
  period: PeriodInfo,
  previousPeriod: PeriodInfo,
  current: ActivityFigures,
  previous: ActivityFigures,
  /** Lignes de travaux réalisées dans la période, par catégorie d'entretien (dossiers distincts). */
  categories: z.array(z.object({ category: MaintenanceCategory, lines: Count, interventions: Count })),
  /** Dossiers restitués dans la période, par mécanicien attribué (`null` : non attribué). */
  mechanics: z.array(z.object({ membershipId: Id.nullable(), name: z.string().nullable(), interventions: Count })),
  /** Volume financier saisi dans l'outil (même calcul que `metrics.atelier`), période et précédente. */
  finance: z.object({
    current: AtelierFigures,
    previous: AtelierFigures,
    /** Mois de la période (exercice, année) ou 12 mois jusqu'au mois choisi, le plus ancien d'abord. */
    months: z.array(z.object({ key: MonthKey, figures: AtelierFigures })),
    pendingDecision: z.object({ count: Count, amountTtc: amount() }),
  }),
  /** Facturé réel : relu depuis Pennylane (prévu). Toujours `null`. */
  invoicing: z.null(),
  generatedAt: z.iso.datetime({ offset: true }),
});
export type ManagementActivityOutput = z.infer<typeof ManagementActivityOutput>;
